// GET /products/facets (#226 / E4-S10) — the values the Brand and Vendor filter chips are
// drawn from. Computed by the server over the whole ACTIVE catalogue, deliberately NOT over
// the current result page: a chip list that shrank as you narrowed would let a shopper strand
// themselves on an empty grid with no way to unselect what emptied it.
export interface ProductFacetVendor {
    id: number;
    name: string;
}

export interface ProductFacetsResponse {
    brands: string[];
    vendors: ProductFacetVendor[];
}

// The OUTCOME of the facet read, kept separate from its payload. Brand and Vendor can key their
// visibility off the payload (no chips, no section), but In-Stock has no payload of its own — it
// needs to know whether the endpoint ANSWERED, which is not the same question as whether the
// catalogue happens to have brands in it.
//
// `loaded` is the only state that proves GET /products/facets answered. An API that lacks the
// route (a 404) or fails lands on `failed` like any other rejection, and the toggle is withheld
// for it — rendered on any other state it could latch ON against a query that ignores the key.
//
//   idle     nothing requested yet (no ?q, or the first render before the effect runs)
//   loading  request in flight
//   loaded   request resolved 2xx — INCLUDING an empty { brands: [], vendors: [] }
//   failed   request rejected: 404, 5xx or a network failure alike
export type ProductFacetsStatus = "idle" | "loading" | "loaded" | "failed";

// Whether to offer the In-Stock filter at all. One home for the policy because it has two callers
// — the desktop rail and the mobile sheet — and editing one without the other would put the lying
// control back in exactly one of them.
//
// `inStockOnly` is the same exception the brand and vendor chips make: a filter that is already
// narrowing the grid keeps a control that can turn it off, even when the facet read is unavailable
// to vouch for it. Callers on a buffered surface must pass the COMMITTED value, not their draft —
// see FilterSheet.
export const shouldOfferInStockFilter = (
    status: ProductFacetsStatus,
    inStockOnly: boolean,
): boolean => status === "loaded" || inStockOnly;

// The price slider's stops, from the mockup (Shop _ Search Results _ Filter Sidebar) minus its
// leading $0 rung. Each stop is a MAXIMUM — "show me up to $X" — and the last is uncapped.
//
// The $0 stop is deliberately gone (#226 review ruling): a $0 cap can only ever match free
// products, so it was a rung that always emptied the grid. It is NOT remapped to null either —
// two positions holding the same value make the thumb jump, because the index is derived from
// the URL and priceStopIndexFor sends null to the far-right stop.
//
// A fixed ladder rather than a linear $0–$max range because the catalogue's prices bunch at
// the low end: a linear slider would spend most of its travel in a range holding almost no
// products. The uneven gaps ($25 → $50 → $75) are the design's, and are what make the low end
// usable.
export interface PriceStop {
    /** Tick label under the track. */
    label: string;
    /** Max price sent to the API; null = no cap. */
    maxPrice: number | null;
}

export const PRICE_STOPS: PriceStop[] = [
    { label: "$5", maxPrice: 5 },
    { label: "$10", maxPrice: 10 },
    { label: "$25", maxPrice: 25 },
    { label: "$50", maxPrice: 50 },
    { label: "$75", maxPrice: 75 },
    { label: "All", maxPrice: null },
];

// The uncapped stop — the slider's resting position, and the value that means "no price
// filter at all" (so it is never written to the URL).
export const PRICE_STOP_ALL_INDEX = PRICE_STOPS.length - 1;

/** Slider index for a max price from the URL. Unknown values fall back to uncapped. */
export const priceStopIndexFor = (maxPrice: number | null): number => {
    if (maxPrice === null) return PRICE_STOP_ALL_INDEX;
    const index = PRICE_STOPS.findIndex((stop) => stop.maxPrice === maxPrice);
    return index === -1 ? PRICE_STOP_ALL_INDEX : index;
};
